/**
 * Analytics facade — drop-in for the previous `src/services/analytics.js`
 * singleton. Public API (setUser / trackScreen / trackFbGoogleEvents /
 * trackEvent / trackEventCT) is identical, so no call site changes. The
 * CleverTap-equivalent calls now fan out to the providers from the registry
 * (Wisetap only — the CleverTap SDK is removed); the orthogonal sinks
 * (Firebase, FB SDK, Sentry breadcrumbs, Applinks) are unchanged.
 */
import * as Sentry from "@sentry/react-native";
import analytics from "@react-native-firebase/analytics";
import crashlytics from "@react-native-firebase/crashlytics";
import { AppEventsLogger } from "react-native-fbsdk-next";
import { AppLinkEvents, getAppLinkParams } from "@lib/AppLinkEventParams";
import { postApplinkEvent, postApplinkCustomEvent } from "@lib/applinks/api";
// plain async fn (misnamed); aliased off `use*` so rules-of-hooks ignores it
import getLatLng from "@hooks/useLocation";
import { appEnv } from "@config";
import eventPermission from "@constants/eventPermission";
import {
  HOMEPAGE_LOADED,
  IJ_EVENTS,
  LOCATION_EVENTS,
  ORDER_FLOW_EVENTS,
  SIGNUP_FLOW_EVENTS,
} from "@constants/analyticsEventsCT";
import { getProviders } from "./registry";
import type { AnalyticsProvider } from "./AnalyticsProvider";
import type { EventProps, Traits } from "./wisetap/types";

const ADD_TO_CART = "fb_mobile_add_to_cart";
const PLACE_ORDER_SUCCESS = "fb_mobile_purchase";

const fireAppLinkEvent = async (
  event_name: string,
  payload: EventProps,
): Promise<void> => {
  await postApplinkEvent(
    await getAppLinkParams({
      event_name,
      extraAttributionData: payload,
    }),
  );
};

const fanOut = (fn: (p: AnalyticsProvider) => void): void => {
  getProviders().forEach((p) => {
    try {
      fn(p);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("analytics provider error", e);
    }
  });
};

class Analytics {
  setUser = (userId: string | number, payload: Traits = {}): void => {
    fanOut((p) => p.identify(userId, payload));
  };

  trackScreen(_screenName?: string, _payload?: EventProps): void {}

  async trackFbGoogleBranchCustomEvents(
    eventName: string,
    payload: EventProps,
  ): Promise<void> {
    try {
      if (eventName === IJ_EVENTS.IJ_COMPLETED) {
        AppEventsLogger.logEvent("fb_ij_completed", {});
        analytics().logEvent("google_ij_completed");
      }
      if (eventName === HOMEPAGE_LOADED) {
        AppEventsLogger.logEvent("fb_homepage_loaded", {});
        analytics().logEvent("google_homepage_loaded");
      }
      if (eventName === LOCATION_EVENTS.SERVICEABLE_SIGNUP) {
        AppEventsLogger.logEvent("fb_serviceable_signup", {});
        analytics().logEvent("google_serviceable_signup");
      }
      if (eventName === LOCATION_EVENTS.NOT_SERVICEABLE_SIGNUP) {
        AppEventsLogger.logEvent("fb_not_serviceable_signup", {});
        analytics().logEvent("google_not_serviceable_signup");
      }
      if (eventName === LOCATION_EVENTS.SUBMITTED) {
        AppEventsLogger.logEvent("fb_location_submitted", {});
        analytics().logEvent("google_location_submitted");
      }
      if (eventName === SIGNUP_FLOW_EVENTS.SUCCESSFULL_LOGIN) {
        analytics().logEvent("login", payload);
      }
      if (eventName === ORDER_FLOW_EVENTS.ORDER_COMPLETE) {
        const fbPayload: { fb_currency: string; price?: number } = {
          fb_currency: "INR",
        };
        if (payload.collectible_amount) {
          fbPayload.price = parseInt(String(payload.collectible_amount), 10);
        }
        AppEventsLogger.logEvent(PLACE_ORDER_SUCCESS, fbPayload);
        analytics().logEvent("purchase", payload);
        await fireAppLinkEvent(AppLinkEvents.charged, payload);
      }
      if (eventName === ORDER_FLOW_EVENTS.ORDER_CANCELLED) {
        await fireAppLinkEvent(AppLinkEvents.order_cancelled, payload);
      }
      if (eventName === ORDER_FLOW_EVENTS.PRODUCT_ADDED) {
        AppEventsLogger.logEvent(ADD_TO_CART, {
          fb_currency: "INR",
        });
        analytics().logEvent("add_to_cart", payload);
      }
    } catch (e) {
      console.warn(e);
    }
  }

  async trackFbGoogleEvents(
    eventName: string,
    payload?: EventProps,
  ): Promise<void> {
    try {
      let eventNameWithoutSpace = eventName?.replace(/ /g, "_") || eventName;
      eventNameWithoutSpace = eventNameWithoutSpace?.replace(/-/g, "_");
      eventNameWithoutSpace =
        eventNameWithoutSpace?.trim() || eventNameWithoutSpace;
      if (eventNameWithoutSpace && typeof eventNameWithoutSpace === "string") {
        analytics().logEvent(eventNameWithoutSpace);
        AppEventsLogger.logEvent(eventNameWithoutSpace, {});
        this.trackFbGoogleBranchCustomEvents(eventName, payload || {});
      }
    } catch (e) {
      Sentry.captureException(e);
      // eslint-disable-next-line no-console
      console.log("google or fb or branch event error", e);
    }
  }

  async trackEvent(eventName: string, _payload?: EventProps): Promise<void> {
    let payload = _payload;
    if (eventPermission.LOCATION.includes(eventName)) {
      const { lat, lng } = await getLatLng();
      if (payload) {
        payload = { ...payload, lat, lng };
      } else {
        payload = { lat, lng };
      }
      if (appEnv === "production" && lat && lng) {
        postApplinkCustomEvent("cp-location-event", {
          user_id: null,
          user_data: {},
          last_attributed_touch_data: {
            eventName,
            ...payload,
            fetched_at: new Date(),
          },
        });
      }
    }
    if (eventName === ORDER_FLOW_EVENTS.ORDER_COMPLETE) {
      fanOut((p) => p.trackCharged(payload, payload?.cart_items));
      this.trackFbGoogleEvents(eventName, payload);
      return;
    }
    fanOut((p) => p.track(eventName, payload));
    this.trackFbGoogleEvents(eventName, payload);
    Sentry.addBreadcrumb({
      category: "analytics",
      message: eventName,
      data: payload,
    });
    crashlytics().log(eventName);
  }

  trackEventCT = (eventName: string, payload?: EventProps): void => {
    fanOut((p) => p.track(eventName, payload));
  };

  /** Force-send any buffered events (e.g. on backgrounding). */
  flush = (): void => {
    fanOut((p) => p.flush());
  };

  /** Clear per-user analytics state (call on logout). */
  reset = (): void => {
    fanOut((p) => p.reset());
  };
}

const wisetapAnalytics = new Analytics();

export default wisetapAnalytics;
